import React from 'react';
import ReactDOM from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface RollbackConfirmModalProps {
  open: boolean;
  eventName?: string;
  marketName?: string;
  lastResult?: string;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const formatResult = (result?: string) => {
  if (!result || result === '-') return '-';
  if (result === '-999') return 'Cancelled';
  return result;
};

const RollbackConfirmModal: React.FC<RollbackConfirmModalProps> = ({
  open,
  eventName,
  marketName,
  lastResult,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99]">
      <div className="bg-white p-5 rounded-lg shadow-xl w-[95%] max-w-md">
        <div className="flex justify-between gap-x-2">
          <h2 className="font-semibold text-lg mb-2">Confirm Rollback</h2>
          <button onClick={onClose} className="self-start bg-primary/20 rounded-full p-1 shadow">
            <X size={18} />
          </button>
        </div>

        <div className="text-sm text-gray-700 space-y-2">
          <p>Are you sure you want to rollback this market result?</p>
          <p className="font-medium text-gray-900">
            Event: <span className="font-semibold">{eventName || '-'}</span>
          </p>
          <p className="font-medium text-gray-900">
            Market: <span className="font-semibold">{marketName || '-'}</span>
          </p>
          <p className="font-medium text-gray-900">
            Last Result: <span className="font-semibold">{formatResult(lastResult)}</span>
          </p>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            Close
          </Button>
          <Button
            type="button"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-x-2">
                <span className="inline-block animate-spin">
                  <Loader2 className="w-5" />
                </span>
                Rolling back...
              </div>
            ) : (
              'Confirm'
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RollbackConfirmModal;
